<script setup lang="ts">
    import EmployeeAvatar from '@/components/EmployeeAvatar.vue';
    import type { User } from '@/types';
    import {
        Dialog,
        DialogBody,
        DialogClose,
        DialogContent,
        DialogDescription,
        DialogFooter,
        DialogHeader,
        DialogTitle,
        DialogTrigger,
    } from '@/components/ui/dialog';
    import { Button } from '@/components/ui/button';
    import { computed, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import SearchInput from '@/components/SearchInput.vue';

    const { t } = useI18n();

    const props = defineProps<{
        employees?: User[];
        company?: string;
    }>();

    const searchQuery = ref('');

    const filteredEmployees = computed(() => {
        if (!props.employees) return [];
        if (!searchQuery.value) return props.employees;

        const query = searchQuery.value.toLowerCase();
        return props.employees.filter(
            (employee) =>
                employee.name.toLowerCase().includes(query) ||
                employee.email.toLowerCase().includes(query),
        );
    });

    const visibleEmployees = computed(() => {
        return props.employees?.slice(0, 3);
    });

    const remainingCount = computed(() => {
        return Math.max(0, (props.employees?.length ?? 0) - 3);
    });
</script>

<template>
    <Dialog>
        <DialogTrigger as-child>
            <div class="flex cursor-pointer items-center gap-1.5">
                <EmployeeAvatar
                    v-for="(employee, index) in visibleEmployees"
                    :key="index"
                    :user="employee"
                    class="-ml-3 first:ml-0"
                />
                <div
                    v-if="remainingCount > 0"
                    class="text-xs"
                >
                    +{{ remainingCount }}
                </div>
            </div>
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>{{ t('companies.employees_assigned') }} {{ company }}</DialogTitle>
            </DialogHeader>

            <DialogBody class="flex flex-col gap-5">
                <SearchInput
                    v-model="searchQuery"
                    :placeholder="t('forms.search')"
                />
                <span class="text-muted-foreground text-xs">
                    <span v-if="searchQuery !== ''">
                        {{ filteredEmployees?.length }} {{ t('datatables.of') }}
                    </span>
                    {{ employees?.length }} {{ t('employee.employees') }}
                </span>

                <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div
                        v-for="employee in filteredEmployees"
                        :key="employee.id"
                        class="flex items-center gap-4"
                    >
                        <EmployeeAvatar
                            :user="employee"
                            class="size-8"
                        />
                        <div class="flex flex-col gap-1">
                            <div class="text-xs leading-none">{{ employee.name }}</div>
                            <a
                                :href="`mailto:${employee.email}`"
                                class="text-secondary text-xs leading-none hover:underline"
                            >
                                {{ employee.email }}
                            </a>
                        </div>
                    </div>
                </div>
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                    >
                        {{ t('forms.close') }}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
